import useOverlayBadge from "@insite/client-framework/Common/Hooks/useOverlayBadge";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getIsProductCompareFull,
    getProductCompareChecked,
} from "@insite/client-framework/Store/Components/CompareProductsDrawer/CompareProductsDrawerSelectors";
import addProductToCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/AddProductToCompare";
import removeProductFromCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/RemoveProductFromCompare";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import Badge from "@insite/mobius/Badge";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { selectItem } from "@WhiteCap/Common/Utillities/CustomTracking";
import { extractCategoriesFromPath, getCategoryListInfo } from "@WhiteCap/Common/Utillities/utils";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showImage: boolean;
    showCompare: boolean;
    extendedStyles?: ProductListProductImageStyles;
    showTextBadge?: boolean;
    showImageBadge?: boolean;
}

const mapStateToProps = (state: ApplicationState, props: HasProductContext) => ({
    settingsCollection: getSettingsCollection(state),
    productCompareChecked: getProductCompareChecked(state, props.productContext),
    isProductCompareFull: getIsProductCompareFull(state),
});

const mapDispatchToProps = {
    addProductToCompare,
    removeProductFromCompare,
};

type Props = ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    OwnProps &
    HasProductContext;

export interface ProductListProductImageStyles {
    wrapper?: InjectableCss;
    productImage?: ProductImageStyles;
    compareCheckbox?: CheckboxPresentationProps;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
}

export const productImageStyles: ProductListProductImageStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            align-items: center;
        `,
    },
    compareCheckbox: {
        css: css`
            align-self: center;
            padding-top: 20px;
            label {
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            margin-left: 3px !important;
                        `,
                        css``,
                        css``,
                        css``,
                        css``,
                    ])}
            }
        `,
    },
    productImage: {
        image: {
            css: css`
                img {
                    min-width: 100%;
                }
                position: absolute;
                top: 0;
                left: 0;
                z-index: 0;
            `,
        },
    },
    badgeAreaWrapper: {
        css: css`
            position: relative;
        `,
    },
    badgeTextWrapper: {
        css: css`
            position: absolute;
            width: 50%;
            white-space: nowrap;
            overflow: hidden;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            width: 75%;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    badgeImageWrapper: {
        css: css`
            position: absolute;
            width: 25%;
            height: 25%;
        `,
    },
    badgeText: {
        weight: "bold",
        css: css`
            width: 100%;
            margin: auto;
            ${wrapInContainerStyles}
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            font-size: 10px;
                        `,
                    ],
                    "max",
                )}
        `,
    },
};

const ProductListProductImage = ({
    productContext,
    showImage,
    showTextBadge,
    showImageBadge,
    showCompare,
    isProductCompareFull,
    settingsCollection,
    productCompareChecked,
    extendedStyles,
    addProductToCompare,
    removeProductFromCompare,
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [styles] = useState(() => mergeToNew(productImageStyles, extendedStyles));

    const {
        showOverlayBadge,
        overlayBadge,
        overlayBadgeWrapperStyles,
        badgeAreaWrapperRef,
        overlayBadgeWrapperClickHandler,
    } = useOverlayBadge({
        productContext,
        showTextBadge,
        showImageBadge,
        styles,
    });

    const handleChanged = (_: React.SyntheticEvent, value: boolean) => {
        if (value && isProductCompareFull) {
            toasterContext.addToast({
                body: siteMessage("ProductionCompare_MaximumItemsReached"),
                messageType: "warning",
            });
        } else if (value) {
            addProductToCompare({ productId: productContext.product.id });
        } else {
            removeProductFromCompare({ productId: productContext.product.id });
        }
    };
    const categories = extractCategoriesFromPath(productContext.productInfo?.productDetailPath || "");
    const { listName, listId } = getCategoryListInfo(categories);

    const itemSelected = () => {
        if (productContext) {
            selectItem({
                event: "select_item",
                ecommerce: {
                    currency: "USD",
                    item_list_name: listName,
                    item_list_id: listId,
                    items: [
                        {
                            index: productContext.idx,
                            item_id: productContext.product.productNumber,
                            item_name: productContext.product.productTitle,
                            item_brand: productContext.product.brand?.name ? productContext.product.brand?.name : "",
                            item_category: categories[0] || "",
                            item_category2: categories[1] || "",
                            item_category3: categories[2] || "",
                            item_variant:
                                productContext.product.variantTraits &&
                                productContext.product.variantTraits.length > 0 &&
                                productContext.product.variantTraits[0].traitValues &&
                                productContext.product.variantTraits[0].traitValues.length > 0
                                    ? productContext.product.variantTraits[0].traitValues[0].valueDisplay
                                    : "Standard",
                            price: productContext.product.unitListPrice,
                            discount: 0.0,
                            quantity: 1,
                            coupon:
                                productContext?.product?.badges && productContext?.product?.badges.length > 0
                                    ? productContext?.product?.badges[0].name
                                    : "",
                        },
                    ],
                },
            });
        }
    };
    return (
        <StyledWrapper
            {...styles.wrapper}
            data-test-selector="productListProductImage"
            data-test-key={productContext.product.id}
        >
            {showImage && !showOverlayBadge && (
                <ProductImage extendedStyles={styles.productImage} product={productContext} onClick={itemSelected} />
            )}
            {showImage && showOverlayBadge && overlayBadge && (
                <StyledWrapper
                    {...styles.badgeAreaWrapper}
                    ref={badgeAreaWrapperRef}
                    data-test-selector="productListBadge"
                >
                    <ProductImage extendedStyles={styles.productImage} product={productContext} />
                    <StyledWrapper {...overlayBadgeWrapperStyles} onClick={overlayBadgeWrapperClickHandler}>
                        <Badge
                            type={overlayBadge.badgeType}
                            value={
                                overlayBadge.badgeType === "Image"
                                    ? overlayBadge.largeImageBadgePath
                                    : overlayBadge.displayText
                            }
                            badgeTextColor={overlayBadge.textColorHexCode}
                            badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                            badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0}
                            imageAltText={overlayBadge.imageAltText ? overlayBadge.imageAltText : overlayBadge.name}
                            textBadgeCss={styles.badgeText}
                            imageBadgeProps={styles.badgeImageProps}
                        />
                    </StyledWrapper>
                </StyledWrapper>
            )}
            {showCompare && settingsCollection.productSettings.enableProductComparisons && (
                <Checkbox
                    {...styles.compareCheckbox}
                    checked={productCompareChecked}
                    onChange={handleChanged}
                    data-test-selector="productList_CompareCheckbox"
                >
                    {translate("Compare")}
                </Checkbox>
            )}
        </StyledWrapper>
    );
};

export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductListProductImage));
